import { useState, useEffect } from "react"
import { useFirebaseAuthContext } from "../firebase-auth-contex"
import { useConfigurationContext } from "contexts/configuration/context"

interface IStellaCustomTokenResponse {
  token: string
  uid: string
}

export const useStellaCustomToken = (): IStellaCustomToken => {
  const [customToken, setCustomToken] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)

  const { user } = useFirebaseAuthContext()
  const configuration = useConfigurationContext()

  useEffect(() => {
    const generateCustomToken = async () => {
      if (!user) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(undefined)

        // Get the user's email and UID from the broker portal's Firebase auth
        const uid = user.uid
        const email = user.email
        const displayName = user.displayName

        // Get the functions URL and API key from configuration
        const functionsUrl = configuration.aiPolicyAssistant.functionsUrl
        const apiKey = configuration.aiPolicyAssistant.apiKey

        if (!functionsUrl) {
          throw new Error(
            "Stella AI Agent functions URL is not configured. Please add 'functionsUrl' to your aiPolicyAssistant configuration."
          )
        }

        if (!apiKey) {
          throw new Error(
            "Stella AI Agent API key is not configured. Please add 'apiKey' to your aiPolicyAssistant configuration."
          )
        }

        // Call the Stella AI Agent's HTTP endpoint to create a custom token
        const response = await fetch(`${functionsUrl}/createCustomToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": apiKey
          },
          credentials: "include",
          body: JSON.stringify({
            uid: uid,
            customClaims: {
              email: email,
              name: displayName,
              // Add any additional claims you want to pass to Stella
              portalUser: true,
              source: "broker-portal"
            }
          })
        })

        if (!response.ok) {
          throw new Error(`Failed to create custom token: ${response.status}`)
        }

        const data: IStellaCustomTokenResponse = await response.json()
        setCustomToken(data.token)
      } catch (err: unknown) {
        console.error("Error generating Stella custom token:", err)
        setError((err as Error).message || "Failed to generate custom token")
      } finally {
        setIsLoading(false)
      }
    }

    generateCustomToken()
  }, [
    user,
    configuration.aiPolicyAssistant.functionsUrl,
    configuration.aiPolicyAssistant.apiKey
  ])

  return {
    customToken,
    isLoading,
    error
  }
}

interface IStellaCustomToken {
  customToken: string | undefined
  isLoading: boolean
  error: string | undefined
}
